/**
 * Module dependencies.
 */

import { Svg } from '@untile/react-core/components/svg';
import { media } from '@untile/react-core/styles/media';
import { useCallback, useMemo } from 'react';
import { Control, FormState, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSettingsProfileOptions } from 'src/api/me/settings-profile-options';
import { UpdateUserFormData } from 'src/api/me/update-user';
import infoSvg from 'src/assets/16/circle-info.svg';
import { InputField } from 'src/components/core/forms/input/field';
import { RadioGroupField } from 'src/components/core/forms/radio-group/field';
import { SelectField } from 'src/components/core/forms/select/field';
import { FloatingLabelStyle, FormGrid } from 'src/components/core/forms/styles';
import { Tooltip } from 'src/components/core/tooltip';
import { UnsavedChangesDialog } from 'src/components/modals/unsaved-changes-dialog';
import { requiredRule } from 'src/core/utils/fields-rules';
import { pickNonNil } from 'src/core/utils/forms';
import { UserFormDataRaw } from 'src/pages/definicoes/perfil-da-empresa';
import { useSession } from 'src/providers/session';
import { NormalizedSettingsProfileOptions } from 'src/types/settings';
import styled from 'styled-components';

import { SubmitButton } from './submit-button';

/**
 * `Props` type.
 */

type Props = {
  onSubmit: (data: UpdateUserFormData, reset: (data?: Partial<UserFormDataRaw>) => void) => void;
  shouldConfirmLeave?: boolean;
  submitLabel?: string;
};

/**
 * `ProfileFieldProps` type.
 */

type ProfileFieldProps = {
  control: Control<any, any, any>;
  formState: FormState<any>;
  hideIsFamiliarCompany?: boolean;
  options: NormalizedSettingsProfileOptions;
};

/**
 * `formFields` constant.
 */

const formFields = ['companyCae', 'companyName', 'companyType', 'companyYear', 'isFamiliarCompany', 'locality'];

/**
 * `StyledTooltip` styled component.
 */

const StyledTooltip = styled(Tooltip)`
  flex: 1;
`;

/**
 * `Label` styled component.
 */

const Label = styled(FloatingLabelStyle)`
  display: block;
  flex: 1;
  padding: 7px 0 0;
`;

/**
 * Export `StyledRadioGroupField` styled component.
 */

export const StyledRadioGroupField = styled(RadioGroupField)`
  ${media.min.sm`
    display: grid;
    grid-template-areas: 'label input' 'error error';
    grid-template-columns: 1fr auto;
  `}

  > label {
    grid-area: label;
  }

  ul {
    align-self: flex-end;
    display: flex;
    grid-area: input;
    grid-gap: var(--gutter);
  }

  [role='alert'] {
    grid-area: error;
    padding: 0;
  }
` as typeof RadioGroupField;

/**
 * `InfoIcon` styled component.
 */

const InfoIcon = styled(Svg).attrs({
  icon: infoSvg,
  size: '16px'
})`
  margin-left: 6px;
  top: 2px;
`;

/**
 * Export `IsFamiliarCompanyField` component.
 */

export function IsFamiliarCompanyField(props: Pick<ProfileFieldProps, 'control' | 'formState'>) {
  const { control, formState } = props;
  const { t } = useTranslation(['common', 'settings']);
  const yesNoOptions = useMemo(
    () => [
      {
        label: t('common:form.labels.yes'),
        value: 'true'
      },
      {
        label: t('common:form.labels.no'),
        value: 'false'
      }
    ],
    [t]
  );

  return (
    <StyledRadioGroupField
      control={control as any}
      disabled={formState.isSubmitting}
      label={
        <StyledTooltip
          description={t('common:labels.familyOwnedInfo.description')}
          placement={'top-end'}
          title={t('common:labels.familyOwnedInfo.title')}
        >
          <Label>
            {t('common:labels.familyOwned')}

            <InfoIcon />
          </Label>
        </StyledTooltip>
      }
      name={'isFamiliarCompany'}
      options={yesNoOptions}
      rules={requiredRule(t)}
    />
  );
}

/**
 * Export `ProfileFormFields` component.
 */

export function ProfileFormFields(props: ProfileFieldProps) {
  const { control, formState, hideIsFamiliarCompany, options } = props;
  const { t } = useTranslation(['common', 'settings']);
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 1;
  const minYear = 1500;

  const rules = useMemo(
    () => ({
      required: requiredRule(t),
      year: {
        max: {
          message: t('common:form.errors.max', { size: maxYear }),
          value: maxYear
        },
        min: {
          message: t('common:form.errors.min', { size: minYear }),
          value: minYear
        }
      }
    }),
    [maxYear, t]
  );

  return (
    <>
      <InputField
        control={control}
        disabled={formState.isSubmitting}
        label={t('settings:profile.form.labels.name')}
        name={'companyName'}
        rules={rules.required}
      />

      <InputField
        control={control}
        disabled={formState.isSubmitting}
        label={t('settings:profile.form.labels.year')}
        max={currentYear}
        min={minYear}
        name={'companyYear'}
        rules={{ ...rules.required, ...rules.year }}
        type={'number'}
      />

      <SelectField
        control={control}
        disabled={formState.isSubmitting}
        label={t('settings:profile.form.labels.type')}
        name={'companyType'}
        options={options.companyType}
        rules={rules.required}
      />

      {!hideIsFamiliarCompany && <IsFamiliarCompanyField control={control} formState={formState} />}

      <SelectField
        control={control}
        disabled={formState.isSubmitting}
        label={t('settings:profile.form.labels.cae')}
        name={'companyCae'}
        options={options.companyCae}
        rules={rules.required}
      />

      <SelectField
        control={control}
        disabled={formState.isSubmitting}
        label={t('settings:profile.form.labels.location')}
        name={'locality'}
        options={options.locality}
        rules={rules.required}
      />
    </>
  );
}

/**
 * `ProfileForm` component.
 */

export function ProfileForm(props: Props) {
  const { onSubmit, shouldConfirmLeave, submitLabel } = props;
  const { user } = useSession();
  const { data } = useSettingsProfileOptions();
  const options = data as NormalizedSettingsProfileOptions;
  const defaultValues = pickNonNil(user, formFields);
  const { control, formState, handleSubmit, reset } = useForm<UserFormDataRaw>({
    defaultValues: {
      ...defaultValues,
      isFamiliarCompany:
        typeof defaultValues.isFamiliarCompany === 'boolean'
          ? String(defaultValues.isFamiliarCompany)
          : String(defaultValues.isFamiliarCompany !== '0')
    },
    mode: 'onSubmit'
  });

  const handleOnSubmit = useCallback(
    (formData: UserFormDataRaw) => {
      return onSubmit(
        {
          ...formData,
          isFamiliarCompany: formData.isFamiliarCompany === 'true'
        },
        reset
      );
    },
    [onSubmit, reset]
  );

  return (
    <FormGrid as={'form'} onSubmit={handleSubmit(handleOnSubmit)}>
      <ProfileFormFields control={control as Control<any>} formState={formState} options={options} />

      <SubmitButton
        disabled={formState.isSubmitting}
        hiddenVisually={!formState.isDirty && !!shouldConfirmLeave}
        isLoading={formState.isSubmitting}
        onClick={handleSubmit(handleOnSubmit)}
        submitLabel={submitLabel}
      />

      <UnsavedChangesDialog shouldConfirmLeave={formState.isDirty && !!shouldConfirmLeave} />
    </FormGrid>
  );
}
